import { Button, DropdownDivider, DropdownItem, Heading, HFlow, Icon, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import useSession from 'components/auth/useSession'
import { Box } from 'components/Box'
import { DateTime } from 'components/date'
import { Age } from 'components/date/Age'
import { DropdownButton } from 'components/dropdown'
import { useErrorHandler } from 'components/error'
import { toEsusParameters } from 'components/esus'
import { Cpf, Telefone } from 'components/label'
import { confirm } from 'components/modals/confirm'
import { useExcluirCidadaoMutation, useReativarCidadaoMutation } from 'graphql/hooks.generated'
import qs from 'qs'
import React from 'react'
import { useHistory, useLocation } from 'react-router'
import { identidadeGenero, sexo } from 'types/enums'
import Permissions from 'types/Permissions'
import { titleCase } from 'util/strConversor'

import { CidadaoListingFormModel } from '../types/CidadaoPesquisarFormModel'
import { CidadaoUrlParams } from '../types/CidadaoUrlParams'

interface CidadaoListingItemProps {
  data: CidadaoListingFormModel
  refetchLocal(): void
  cidadaoUrlParams: CidadaoUrlParams
  setIsModalJustificativaOpen: () => void
  handleCidadaoIdJustificativa: (cidadaoId: string) => void
}

export const CidadaoListingItem = (props: CidadaoListingItemProps) => {
  const {
    data,
    cidadaoUrlParams: { callbackUrl, callbackParams },
  } = props

  const { hasAuthorization } = useSession()
  const [excluirCidadao] = useExcluirCidadaoMutation()
  const [reativarCidadao] = useReativarCidadaoMutation()
  const externalAccess = !!callbackUrl

  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const location = useLocation()
  const history = useHistory()

  const telefone = data.telefoneCelular
    ? data.telefoneCelular
    : data.telefoneResidencial
    ? data.telefoneResidencial
    : null

  const cadastrarEEditar = hasAuthorization(Permissions.gestaoDeCadastrosDeCidadao.visualizarCidadao.cadastrarEEditar)
  const excluir = hasAuthorization(Permissions.gestaoDeCadastrosDeCidadao.visualizarCidadao.excluir)
  const inativar = hasAuthorization(Permissions.gestaoDeCadastrosDeCidadao.visualizarCidadao.inativar)
  const visualizarProntuario = hasAuthorization(Permissions.visualizarProntuario)
  const visualizarAgenda = hasAuthorization(Permissions.visualizarAgenda)

  const hasAnyPermission = cadastrarEEditar || excluir || inativar || visualizarProntuario || visualizarAgenda

  const onExcluirClick = () => {
    confirm({
      title: `Deseja excluir o cadastro do cidadão?`,
      confirmLabel: 'Excluir',
      type: 'danger',
      onConfirm: () => {
        return excluirCidadao({ variables: { cidadaoId: data.id } })
          .then(() => {
            alert('success', 'Cadastro do cidadão excluído')
            props.refetchLocal()
          })
          .catch(handleRejection)
      },
    })()
  }

  const onVisualizarClick = () => {
    history.push(`/cidadao/${data.id}${location.search}`)
  }

  const onEditarClick = () => {
    history.push(`/cidadao/${data.id}/edit${location.search}`)
  }

  const navigateToExternalUrl = () => {
    if (callbackUrl === 'lista-atendimento' || callbackUrl === 'ad/lista/cadastro') {
      history.push(`/${callbackUrl}?cidadaoId=${data.id}`)
    } else {
      //callback para módulos antigos - remover toda a condição quando os módulos serem refeitos
      const url = atob(callbackUrl)
      const decodedParams = atob(callbackParams)
      const path = url === '/pec/user/registrotardiolisting/registrotardiodetail' ? 'registroTardio' : 'agenda'
      history.push(
        `/${path}?iframeUrl=${url}?${btoa(decodedParams + '&' + qs.stringify({ cidadaoCadastradoId: data.id }))}`
      )
    }
  }

  const onReativarClick = () => {
    confirm({
      title: `Deseja reativar o cadastro do cidadão?`,
      confirmLabel: 'Reativar',
      type: 'primary',
      onConfirm: () => {
        return reativarCidadao({ variables: { cidadaoId: data.id } })
          .then(() => {
            alert('success', 'Cadastro do cidadão reativado')
            props.refetchLocal()
          })
          .catch(handleRejection)
      },
    })()
  }

  const onVisualizarProntuarioClick = () => {
    props.handleCidadaoIdJustificativa(data.id)
    props.setIsModalJustificativaOpen()
  }

  const handleVerAgendamentosClick = () => {
    history.push(
      `/cidadao/visualizarAgendamento?${toEsusParameters({
        cidadaoId: data.id,
        cidadaoNome: data.nome,
        prontuarioId: data.prontuario.id,
        pecBackLink: location.pathname + location.search,
        showCidadaoLink: 0,
      })}`
    )
  }
  return (
    <>
      <Box style={{ minHeight: 136, textAlign: 'left' }}>
        <HFlow justifyContent='space-between'>
          <VFlow vSpacing={0}>
            <HFlow hSpacing={0.5} alignItems='baseline'>
              <Heading level={3}>{titleCase(data.nomeSocial ? data.nomeSocial : data.nome)}</Heading>
              {data.nomeSocial && <Text fontWeight='bold'>(Nome social)</Text>}
              {data.nomeSocial && <Text>{'| ' + titleCase(data.nome)}</Text>}
              {data.faleceu && <Tag>Óbito</Tag>}
              {!data.ativo && <Tag>Inativo</Tag>}
              {data.unificado && <Tag>Cadastro unificado</Tag>}
            </HFlow>
            <HFlow hSpacing={2}>
              <VFlow vSpacing={0} style={{ width: 350 }}>
                <HFlow>
                  <HFlow alignItems='center' hSpacing={0.5}>
                    <Text fontWeight='bold'>CPF</Text>
                    {data.cpf ? <Cpf value={data.cpf} /> : '-'}
                  </HFlow>
                  <HFlow hSpacing={0.5}>
                    <Text fontWeight='bold'>CNS</Text>
                    {data.cns ? <Text>{data.cns}</Text> : '-'}
                  </HFlow>
                </HFlow>
                <HFlow alignItems='center' hSpacing={0.5}>
                  <Text fontWeight='bold'>Data de nascimento</Text>
                  <DateTime format='DD/MM/YYYY' value={data.dataNascimento} />
                  <Age dateOfBirth={data.dataNascimento} />
                </HFlow>
                <HFlow hSpacing={0.5}>
                  <Text fontWeight='bold'>Sexo</Text>
                  <Text>{sexo[data.sexo]}</Text>
                  <Text fontWeight='bold'>{data.identidadeGeneroDbEnum ? 'Id. de gênero' : null}</Text>
                  <Text>{data.identidadeGeneroDbEnum && identidadeGenero[data.identidadeGeneroDbEnum]}</Text>
                </HFlow>
                <HFlow alignItems='center' hSpacing={0.5}>
                  <Text fontWeight='bold'>Última atualização</Text>
                  <DateTime format='DD/MM/YYYY' value={data.dataAtualizado ? data.dataAtualizado : '-'} />
                </HFlow>
              </VFlow>
              <VFlow vSpacing={0}>
                <HFlow hSpacing={0.5}>
                  <Text fontWeight='bold'>Nome da mãe</Text>
                  <Text>{data.nomeMae ? titleCase(data.nomeMae) : '-'}</Text>
                </HFlow>
                <HFlow alignItems='center' hSpacing={0.5}>
                  <Text fontWeight='bold'>Telefone</Text>
                  {telefone ? <Telefone value={telefone}></Telefone> : <Text>-</Text>}
                </HFlow>
                <HFlow hSpacing={0.5}>
                  <Text fontWeight='bold'>Unidade responsável</Text>
                  <Text>{titleCase(data.cidadaoVinculacaoEquipe?.unidadeSaude?.nome || '-')}</Text>
                </HFlow>
                <HFlow hSpacing={0.5}>
                  <Text fontWeight='bold'>Município de nascimento</Text>
                  <Text>
                    {data.localidadeNascimento?.nome
                      ? titleCase(data.localidadeNascimento?.nome) +
                        ' - ' +
                        titleCase(data.localidadeNascimento?.uf?.sigla)
                      : '-'}
                  </Text>
                </HFlow>
              </VFlow>
            </HFlow>
          </VFlow>
          <HFlow alignItems='center' hSpacing={0.5}>
            {externalAccess ? (
              <Button kind='normal' skin='outline' size='small' onClick={navigateToExternalUrl}>
                <Icon icon='plus' style={{ marginRight: '0.5rem' }} />
                Selecionar e voltar
              </Button>
            ) : (
              <Button kind='normal' skin='outline' size='small' onClick={onVisualizarClick}>
                <Icon icon='zoomOutline' style={{ marginRight: '0.5rem' }} />
                Visualizar
              </Button>
            )}
            {hasAnyPermission && (
              <DropdownButton>
                {externalAccess && <DropdownItem onClick={onVisualizarClick}>Visualizar</DropdownItem>}

                {cadastrarEEditar && (
                  <Tooltip text={!data?.ativo && 'Este cadastro não pode ser atualizado pois está inativo'}>
                    <DropdownItem disabled={!data.ativo} onClick={onEditarClick}>
                      Atualizar cadastro
                    </DropdownItem>
                  </Tooltip>
                )}

                {!data.ativo && inativar && !externalAccess && (
                  <DropdownItem onClick={onReativarClick}>Reativar cadastro</DropdownItem>
                )}

                {visualizarProntuario && !externalAccess && (
                  <Tooltip text={!data.prontuario && 'Cadastro não possui prontuário'}>
                    <DropdownItem onClick={onVisualizarProntuarioClick} disabled={!data.prontuario}>
                      Ver prontuário
                    </DropdownItem>
                  </Tooltip>
                )}

                {visualizarAgenda && !externalAccess && (
                  <Tooltip text={!data.possuiAgendamento && 'Não há agendamentos registrados para o cidadão'}>
                    <DropdownItem onClick={handleVerAgendamentosClick} disabled={!data.possuiAgendamento}>
                      Ver agendamentos
                    </DropdownItem>
                  </Tooltip>
                )}

                {excluir && !externalAccess && (
                  <>
                    <DropdownDivider />
                    <DropdownItem
                      type='danger'
                      style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem', display: 'flex', alignItems: 'center' }}
                      onClick={onExcluirClick}
                    >
                      <Icon icon='trashOutline' style={{ marginRight: '0.5rem' }} /> Excluir
                    </DropdownItem>
                  </>
                )}
              </DropdownButton>
            )}
          </HFlow>
        </HFlow>
      </Box>
    </>
  )
}
